import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslocoModule } from '@ngneat/transloco';

import { AuthService } from 'app/core/auth/auth.service';
import { OtpInputComponent } from 'app/shared/components/otp-input/otp-input.component';

/**
 * Datos OPCIONALES del modal de step-up.
 *
 * `actionLabel` es una etiqueta legible de la acción que disparó el 403
 * ("Resetear la contraseña de Juan Pérez"): la publica el call-site en el
 * HttpContext, el interceptor la reenvía y acá sólo se muestra. Es UI pura —
 * nunca un id, nunca un secreto—. Si no llega, el modal se comporta como antes.
 *
 * El código TOTP JAMÁS viaja por acá: `data` va del llamador AL modal, no al revés.
 */
export interface StepUpMfaDialogData {
  actionLabel?: string | null;
}

/**
 * Modal de step-up MFA CANÓNICO y transversal (LION-520).
 *
 * Vive en core/auth porque cualquier feature que necesite revalidar el segundo
 * factor (admin-reset, LION-533/538, ...) lo consume a través del StepUpService
 * y el interceptor. Toma como base el modal de vehículos (LION-534) —el de
 * sign-in es un form de PÁGINA sin validación de 6 dígitos, no sirve de molde—.
 *
 * Pide el código TOTP de 6 dígitos, revalida vía AuthService.stepUpMfa() (que
 * persiste el accessToken reemitido con mfaVerifiedAt fresco) y cierra con
 * `true` si verificó, `false` si se cancela o falla.
 *
 * Seguridad del código MFA (secreto efímero):
 *  - NUNCA se loguea (ni en console ni en toasts: el error usa un i18n fijo).
 *  - NUNCA se pasa por MatDialogConfig.data, queryParams ni navegación.
 *  - El FormControl se resetea al cerrar para no dejarlo en memoria/DOM.
 */
@Component({
  selector: 'app-step-up-mfa-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    TranslocoModule,
    OtpInputComponent
  ],
  template: `
    <div
      class="relative flex h-full max-h-[100dvh] w-full flex-col sm:min-w-[420px] sm:max-w-[520px]"
    >
      @if (isLoading()) {
        <!-- La barra superior es REFUERZO del spinner del botón, no el único
             indicador: el foco del usuario está en el pie, no en el borde. -->
        <div class="absolute inset-x-0 top-0">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        </div>
      }

      <!-- ── Header ──────────────────────────────────────────────────────── -->
      <div class="flex shrink-0 items-start gap-3 px-5 pb-4 pt-[18px]">
        <div
          class="flex h-9 w-9 flex-none items-center justify-center rounded-md border border-primary-100 bg-primary-50 text-primary-600 dark:border-primary-600/40 dark:bg-primary-600/10"
        >
          <mat-icon
            class="icon-size-5 text-current"
            svgIcon="heroicons_outline:shield-check"
          ></mat-icon>
        </div>

        <div class="min-w-0 flex-1">
          <h2
            class="m-0 text-pretty text-lg font-bold leading-[22px] tracking-[-0.01em] text-gray-900 dark:text-gray-100"
          >
            {{ 'stepUpMfa.title' | transloco }}
          </h2>
          <p class="mb-0 mt-1 text-pretty text-md leading-5 text-gray-600 dark:text-gray-400">
            {{ 'stepUpMfa.description' | transloco }}
          </p>
        </div>

        <button
          class="-mr-2 flex-none text-gray-400 !h-11 !w-11 !min-h-11 !min-w-11"
          mat-icon-button
          type="button"
          data-test="step-up-close"
          [attr.aria-label]="'stepUpMfa.cancel' | transloco"
          (click)="close()"
        >
          <mat-icon class="icon-size-4" svgIcon="heroicons_outline:x-mark"></mat-icon>
        </button>
      </div>

      <!-- ── Body ────────────────────────────────────────────────────────── -->
      <form
        [formGroup]="form"
        class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain px-5 pb-5 pt-1"
        data-test="step-up-body"
        (ngSubmit)="confirm()"
      >
        <!-- Contexto de la acción: aditivo. Sin etiqueta no se dibuja nada y el
             modal queda exactamente como antes del rediseño. -->
        @if (actionLabel) {
          <div
            class="flex items-start gap-2.5 rounded-r-md border-y border-r border-l-2 border-gray-100 border-l-primary bg-gray-50 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800"
            data-test="step-up-context"
          >
            <mat-icon
              class="icon-size-4 mt-px flex-none text-gray-400"
              svgIcon="heroicons_outline:lock-closed"
            ></mat-icon>
            <div class="min-w-0">
              <span
                class="mb-0.5 block text-[10.5px] font-bold uppercase leading-none tracking-[0.07em] text-gray-500 dark:text-gray-400"
              >
                {{ 'stepUpMfa.action' | transloco }}
              </span>
              <span class="text-md leading-[19px] text-gray-800 dark:text-gray-200">
                {{ actionLabel }}
              </span>
            </div>
          </div>
        }

        <div class="flex flex-col gap-2">
          <label
            class="text-[12.5px] font-semibold text-gray-700 dark:text-gray-300"
            for="step-up-otp"
          >
            {{ 'stepUpMfa.code' | transloco }}
          </label>

          <!-- OJO: el bloqueo mientras verifica NO va como [disabled] acá. En un
               elemento con formControlName, ese binding lo intercepta la propia
               directiva de reactive forms (warning en dev + disable implícito del
               control). El gate va sobre el FormControl, en confirm(), y llega al
               campo por setDisabledState() del ControlValueAccessor. -->
          <app-otp-input
            id="step-up-otp"
            formControlName="code"
            [length]="6"
            [invalid]="hasError()"
            [ariaLabel]="'stepUpMfa.code' | transloco"
            (completed)="confirm()"
          ></app-otp-input>

          <!-- La ayuda de validez y el error ocupan el MISMO slot: nunca los dos
               a la vez, así el alto del modal no salta al fallar el código. -->
          @if (hasError()) {
            <span
              class="flex items-center gap-1.5 text-[12.5px] font-semibold text-red-700 dark:text-red-400"
              data-test="step-up-error"
              role="alert"
            >
              <mat-icon
                class="icon-size-4 flex-none text-current"
                svgIcon="heroicons_outline:exclamation-triangle"
              ></mat-icon>
              {{ error() | transloco }}
            </span>
          } @else {
            <span
              class="flex items-start gap-1.5 text-sm text-gray-500 dark:text-gray-400"
              data-test="step-up-validity"
            >
              <mat-icon
                class="icon-size-4 mt-px flex-none text-current"
                svgIcon="heroicons_outline:clock"
              ></mat-icon>
              {{ 'stepUpMfa.validity' | transloco }}
            </span>
          }
        </div>
      </form>

      <!-- ── Footer ──────────────────────────────────────────────────────── -->
      <div
        class="flex shrink-0 items-center gap-2.5 border-t border-gray-200 bg-gray-50 px-5 pb-[max(13px,env(safe-area-inset-bottom))] pt-[13px] dark:border-gray-700 dark:bg-gray-900 sm:justify-end"
        data-test="step-up-footer"
      >
        <button
          mat-stroked-button
          type="button"
          data-test="step-up-cancel"
          class="flex-1 !h-12 !min-h-12 sm:!h-10 sm:!min-h-10 sm:!flex-none"
          [disabled]="isLoading()"
          (click)="close()"
        >
          {{ 'stepUpMfa.cancel' | transloco }}
        </button>

        <button
          mat-flat-button
          color="primary"
          type="button"
          data-test="step-up-confirm"
          class="flex-1 !h-12 !min-h-12 sm:!h-10 sm:!min-h-10 sm:!flex-none"
          [disabled]="form.invalid || isLoading()"
          (click)="confirm()"
        >
          @if (isLoading()) {
            <span class="flex items-center gap-1.5">
              <span
                class="h-[13px] w-[13px] animate-spin rounded-full border-2 border-white/40 border-t-white"
              ></span>
              {{ 'stepUpMfa.verifying' | transloco }}
            </span>
          } @else {
            {{ 'stepUpMfa.confirm' | transloco }}
          }
        </button>
      </div>
    </div>
  `,
  // El host queda ENTRE la superficie del MatDialog y el wrapper: sin altura
  // propia, el `h-full` del wrapper resuelve a `auto` y el scroll se escapa al
  // contenedor del modal (el bug de 412px). En desktop la superficie tiene
  // altura automática, así que esto sigue siendo alto por contenido.
  styles: [
    ':host { display: flex; flex-direction: column; min-height: 0; height: 100%; }'
  ]
})
export class StepUpMfaDialogComponent {
  private readonly _dialogRef = inject(MatDialogRef<StepUpMfaDialogComponent, boolean>);
  private readonly _authService = inject(AuthService);
  private readonly _formBuilder = inject(FormBuilder);

  // Opcional a propósito: los llamadores viejos abren el modal sin `data` y el
  // token entonces no está provisto. Sin etiqueta, el bloque no se dibuja.
  private readonly _data = inject<StepUpMfaDialogData | null>(MAT_DIALOG_DATA, {
    optional: true
  });

  readonly actionLabel = this._data?.actionLabel ?? null;

  readonly isLoading = signal(false);

  /**
   * Clave i18n del error, o null. Es SIEMPRE una clave fija: el body del backend
   * (que puede reflejar lo que se tipeó) nunca se muestra.
   */
  readonly error = signal<string | null>(null);
  readonly hasError = computed(() => this.error() !== null);

  @ViewChild(OtpInputComponent) private _otp?: OtpInputComponent;

  // 6 dígitos como el modal de vehículos (LION-534), que pega al MISMO endpoint
  // auth/mfa/step-up ya en producción. El sign-in usa solo `required`, pero acá
  // validamos de verdad los 6 dígitos del TOTP.
  readonly form: FormGroup = this._formBuilder.group({
    code: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]]
  });

  constructor() {
    // El código MFA es un secreto efímero: al cerrar el modal (por cualquier vía)
    // limpiamos el FormControl para no dejarlo residente en memoria/DOM.
    this._dialogRef.afterClosed().subscribe(() => this.form.reset());
  }

  confirm(): void {
    // El guard de isLoading() es lo que evita la request doble: el campo hace
    // autosubmit al sexto dígito y el botón sigue clickeable el mismo tick.
    if (this.form.invalid || this.isLoading()) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);

    const code = this.form.get('code')!;
    // `disable()` NO borra el `.value` de un control suelto (lo que omite los
    // deshabilitados es `FormGroup.value`): el orden acá es sólo de claridad.
    const value = code.value;
    code.disable({ emitEvent: false });

    this._authService.stepUpMfa(value).subscribe({
      next: () => {
        // AuthService.stepUpMfa ya persistió el accessToken fresco; el
        // interceptor global lo adjuntará al reintento de la request original.
        this._dialogRef.close(true);
      },
      error: () => {
        // Mensaje i18n FIJO: nunca reflejamos el body del backend ni el code.
        // Va INLINE y no por toast: el toast aparece lejos del campo que hay que
        // corregir, y el propio modal puede taparlo.
        this.error.set('stepUpMfa.invalid');
        this.isLoading.set(false);
        // El OTP se limpia y recupera el foco para reintentar con el código
        // actual de la app (el anterior ya caducó).
        code.enable({ emitEvent: false });
        code.setValue('');
        this._otp?.focusFirstEmpty();
      }
    });
  }

  close(): void {
    this._dialogRef.close(false);
  }
}
